import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Quote, ChevronLeft, ChevronRight, PenLine, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useTexts } from "@/lib/texts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const Stars = ({ n }) => (
    <span className="flex gap-1" aria-label={`${n} stelle su 5`}>
        {Array.from({ length: n }).map((_, i) => (
            <Star key={i} className="h-3.5 w-3.5 fill-[#E75C15] text-[#E75C15]" />
        ))}
    </span>
);

const inputCls =
    "w-full border border-stone-900/15 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition-colors placeholder:text-stone-400 focus:border-[#E75C15]";

const ReviewForm = ({ onDone }) => {
    const [form, setForm] = useState({ name: "", email: "", project: "", rating: 0, text: "" });
    const [hover, setHover] = useState(0);
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.text.trim() || form.rating === 0) {
            toast.error("Inserisci nome, email, valutazione e testo della recensione.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/reviews`, form);
            toast.success("Grazie! La recensione sarà pubblicata dopo una breve verifica.");
            onDone();
        } catch {
            toast.error("Invio non riuscito. Riprova tra poco.");
        } finally {
            setSending(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="review-form" className="p-8 sm:p-10">
            <DialogTitle className="font-display text-3xl font-light text-stone-900">
                La tua esperienza con noi
            </DialogTitle>
            <p className="mt-2 text-sm text-stone-600">
                La recensione sarà visibile sul sito dopo la verifica da parte nostra.
            </p>

            <div className="mt-8 space-y-5">
                <div>
                    <label htmlFor="review-name" className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                        Nome *
                    </label>
                    <input
                        id="review-name"
                        data-testid="review-input-name"
                        className={inputCls}
                        placeholder="Es. Famiglia Bianchi"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        required
                    />
                </div>
                <div>
                    <label htmlFor="review-email" className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                        Email * <span className="normal-case tracking-normal text-stone-400">(non sarà pubblicata, serve per il ringraziamento)</span>
                    </label>
                    <input
                        id="review-email"
                        type="email"
                        data-testid="review-input-email"
                        className={inputCls}
                        placeholder="latua@email.it"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        required
                    />
                </div>
                <div>
                    <label htmlFor="review-project" className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                        Lavoro realizzato
                    </label>
                    <input
                        id="review-project"
                        data-testid="review-input-project"
                        className={inputCls}
                        placeholder="Es. Restauro rustico · Lucca"
                        value={form.project}
                        onChange={(e) => setForm((f) => ({ ...f, project: e.target.value }))}
                    />
                </div>
                <div>
                    <span className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                        Valutazione *
                    </span>
                    <div className="flex gap-2" onMouseLeave={() => setHover(0)}>
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button
                                type="button"
                                key={n}
                                data-testid={`review-star-${n}`}
                                onClick={() => setForm((f) => ({ ...f, rating: n }))}
                                onMouseEnter={() => setHover(n)}
                                className="p-1 transition-transform hover:scale-110"
                                aria-label={`${n} stelle`}
                            >
                                <Star
                                    className={`h-7 w-7 transition-colors ${
                                        n <= (hover || form.rating)
                                            ? "fill-[#E75C15] text-[#E75C15]"
                                            : "text-stone-300"
                                    }`}
                                />
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label htmlFor="review-text" className="mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500">
                        Recensione *
                    </label>
                    <textarea
                        id="review-text"
                        data-testid="review-input-text"
                        rows={4}
                        className={`${inputCls} resize-none`}
                        placeholder="Raccontaci come è andata..."
                        value={form.text}
                        onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                        required
                    />
                </div>
            </div>

            <button
                type="submit"
                disabled={sending}
                data-testid="review-submit-button"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1C1917] px-6 py-4 text-sm font-medium text-stone-100 transition-colors duration-300 hover:bg-[#B8430D] disabled:opacity-60"
            >
                {sending ? (
                    <>
                        Invio in corso <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                ) : (
                    "Invia la Recensione"
                )}
            </button>
        </form>
    );
};

export const Testimonials = () => {
    const texts = useTexts();
    const [reviews, setReviews] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });

    useEffect(() => {
        axios
            .get(`${API}/reviews`)
            .then((res) => setReviews(res.data))
            .catch(() => setReviews([]));
    }, []);

    useEffect(() => {
        if (!emblaApi || reviews.length < 2) return;
        const id = setInterval(() => emblaApi.scrollNext(), 4500);
        return () => clearInterval(id);
    }, [emblaApi, reviews.length]);

    const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

    const avg = reviews.length
        ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1).replace(".", ",")
        : "5,0";

    return (
        <section
            id="recensioni"
            data-testid="testimonials-section"
            className="relative overflow-hidden bg-[#1C1917] py-20 text-stone-100 sm:py-28 lg:py-36"
        >
            <div className="grain pointer-events-none absolute inset-0" />
            <Quote
                aria-hidden="true"
                className="pointer-events-none absolute -top-10 right-6 h-64 w-64 text-[#E75C15]/5 lg:right-24"
            />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <motion.span
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, ease: EASE }}
                            className="font-mono-alt text-xs uppercase tracking-[0.3em] text-[#E75C15]"
                        >
                            Recensioni
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                            className="mt-4 max-w-2xl font-display text-3xl font-light tracking-tight sm:text-4xl lg:text-5xl"
                        >
                            {texts.testimonials_heading}
                        </motion.h2>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                        className="flex flex-col items-start gap-4 sm:items-end"
                    >
                        <span className="flex items-center gap-3" data-testid="testimonials-rating-summary">
                            <Stars n={5} />
                            <span className="font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-400">
                                {avg} · {reviews.length} recensioni verificate
                            </span>
                        </span>
                        <button
                            data-testid="review-open-button"
                            onClick={() => setDialogOpen(true)}
                            className="group inline-flex items-center gap-2 rounded-full border border-[#E75C15]/50 px-5 py-2.5 text-xs font-medium uppercase tracking-wider text-[#E75C15] transition-colors duration-300 hover:bg-[#E75C15] hover:text-[#1C1917]"
                        >
                            <PenLine className="h-3.5 w-3.5" />
                            Lascia la tua recensione
                        </button>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="relative"
                >
                    <div className="overflow-hidden" ref={emblaRef} data-testid="testimonials-carousel">
                        <div className="flex gap-6">
                            {reviews.map((t) => (
                                <figure
                                    key={t.id}
                                    data-testid={`testimonial-card-${t.id}`}
                                    className="flex min-w-0 shrink-0 grow-0 basis-full flex-col justify-between border border-stone-100/10 bg-stone-100/[0.03] p-7 transition-colors duration-500 hover:border-[#E75C15]/40 sm:basis-[47%] lg:basis-[31.5%]"
                                >
                                    <blockquote className="font-display text-lg font-light italic leading-relaxed text-stone-200">
                                        “{t.text}”
                                    </blockquote>
                                    <figcaption className="mt-6 border-t border-stone-100/10 pt-4">
                                        <Stars n={t.rating} />
                                        <div className="mt-2 text-sm font-medium text-stone-100">{t.name}</div>
                                        {t.project && (
                                            <div className="mt-1 font-mono-alt text-[10px] uppercase tracking-[0.18em] text-stone-500">
                                                {t.project}
                                            </div>
                                        )}
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 flex items-center justify-between">
                        <span className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-stone-500">
                            Trascina o lascia scorrere
                        </span>
                        <div className="flex gap-3">
                            <button
                                data-testid="testimonials-prev"
                                onClick={scrollPrev}
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-stone-100/20 text-stone-300 transition-colors hover:border-[#E75C15] hover:text-[#E75C15]"
                                aria-label="Recensione precedente"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                                data-testid="testimonials-next"
                                onClick={scrollNext}
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-stone-100/20 text-stone-300 transition-colors hover:border-[#E75C15] hover:text-[#E75C15]"
                                aria-label="Recensione successiva"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent
                    data-testid="review-dialog"
                    className="max-h-[90vh] w-[94vw] max-w-xl overflow-y-auto border border-[#E75C15]/30 bg-[#F9F7F2] p-0 sm:rounded-none"
                >
                    <ReviewForm onDone={() => setDialogOpen(false)} />
                </DialogContent>
            </Dialog>
        </section>
    );
};
